'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { DatePickerFloatingContext } from './context/date-picker-floating.context';
import { DatePickerRootContext } from './context/date-picker-root.context';
import {
  datePickerContentPositionerRecipe,
  datePickerContentRecipe,
} from './styles/date-picker-content.styles';
import type { DatePickerContentPropsInterface } from './types/date-picker-content.types';

/**
 * @since 10.0.0
 */
export const DatePickerContent = forwardRef<HTMLDivElement, DatePickerContentPropsInterface>(
  (props: DatePickerContentPropsInterface, forwardedRef) => {
    const { datePicker, modal } = useContext(DatePickerRootContext);
    const floating = useContext(DatePickerFloatingContext);
    const {
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['floating', 'content'],
      initialFocus: 4,
      modal: true,
      returnFocus: true,
      visuallyHiddenDismiss: 'Dismiss date picker',
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([datePicker.refs.setFloating, forwardedRef]);

    const contentPositionerStyles = css(datePickerContentPositionerRecipe.raw({ modal }));
    const contentStyles = css(datePickerContentRecipe.raw({ modal }), cssProp);

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <div
          ref={mergedRefs}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="DatePicker.ContentPositioner"
          className={cx('c11n', 'c11n_date-picker__content-positioner', contentPositionerStyles)}
          style={
            modal
              ? {}
              : {
                  ...datePicker.floatingStyles,
                  visibility: datePicker.middlewareData.hide?.referenceHidden
                    ? 'hidden'
                    : 'visible',
                }
          }
          {...floating.getFloatingProps(rest)}
          id={datePicker.contentId}
        >
          <div
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="DatePicker.Content"
            className={cx('c11n', 'c11n_date-picker__content', contentStyles, className)}
            style={{ ...datePicker.floatingTransitionStyles, ...style }}
          >
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    );
  },
);

DatePickerContent.displayName = 'DatePicker.Content';
